/**
 * Handelt de authenticatie van gebruikers af via Google of anonieme login, inclusief state monitoring en uitloggen.
 */
import {
  signInWithPopup,
  GoogleAuthProvider,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import {
  getAuthErrorKey,
  resolveAuthErrorKey,
} from '@/services/authServiceErrorKeys';

export { getAuthErrorKey, resolveAuthErrorKey };
import { auth } from '@/lib/firebase';
import { setUserProfileDisplayName } from '@/services/userService';
import { handlePostLogin } from '@/services/authServicePostLogin';
import {
  signUpWithEmail,
  sendPasswordResetEmailForUser,
  signInWithEmail,
} from '@/services/authServiceEmailAuth';

export {
  signUpWithEmail,
  sendPasswordResetEmailForUser,
  signInWithEmail,
};
export { reauthenticateUser } from '@/services/authServiceReauth';

/**
 * Service to handle Firebase Authentication operations.
 */

export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    handlePostLogin(result.user, 'google');
    return result;
  } catch (error) {
    console.error('AuthService: Error signing in with Google:', error.message);
    throw error;
  }
};

export const signInAnonymouslyUser = async () => {
  try {
    const result = await signInAnonymously(auth);
    handlePostLogin(result.user, 'anonymous');
    return result;
  } catch (error) {
    console.error('AuthService: Anonymous sign in failed:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('AuthService: Error signing out:', error);
    throw error;
  }
};

/**
 * Subscribes to authentication state changes.
 * @param {(user: any) => void} callback - Function called with the user object or null.
 * @returns {() => void} Unsubscribe function.
 */
export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, (user) => callback(user));
};

/**
 * Writes display name to Firebase Auth and the Firestore user profile.
 * Safe to retry with the same value (idempotent merge).
 * @param {string} displayName
 */
export const persistUserDisplayName = async (displayName) => {
  const user = auth.currentUser;
  if (!user?.uid) {
    throw new Error('Not signed in');
  }
  const trimmed = displayName.trim();
  if (!trimmed || trimmed.length > 50) {
    throw new Error('Invalid display name');
  }
  await updateProfile(user, { displayName: trimmed });
  await setUserProfileDisplayName(user.uid, trimmed);
};
